// Lembra recommendation engine — v1 (sync) + v2 (async DB-backed).
//
// Given a customer brief (tier + team size + dietaries + occasion +
// delivery details), returns a concrete recommendation that the
// approval modal renders. Shared between client + edge functions via
// the @shared Vite alias so we have ONE source of truth for what
// makes a sensible suggestion.
//
// ── v1 (sync, in-code constants) ──────────────────────────────────
//   - Tier → vibe (existing TIER_TO_VIBE mapping)
//   - Team size → bucket → price band (existing PRICING grid)
//   - Bakery picked deterministically per tier:
//       Standard: Cupcake Central (default) or Little Cupcakes (if vegan)
//       Premium:  Le Yeahllow
//       Luxury:   Chéri
//   - Photo URL stubbed per tier (Unsplash); generic copy per bakery
//   - No DB reads — usable everywhere a sync function is OK
//
// ── v2 (async, reads vendor_products) ────────────────────────────
//   - Routes by occasion → vendor_category (cake / hamper / florist /
//     workshop / spa / experience / meal / coffee) using
//     OCCASION_TO_CATEGORY
//   - Queries the DB for the best-fit SKU under the tier ceiling,
//     matching dietaries, with lead time ≤ days remaining. Returns
//     the real product name + price + image + source URL.
//   - MS525: the 65 home-bakery cake SKUs (CC, GL, LC, LY, Chéri)
//     were seeded into vendor_products by migration
//     20260513050000_seed_home_bakery_skus_into_vendor_products.sql.
//     v2 used to short-circuit `cake` to v1 because that seed didn't
//     exist; now the short-circuit is gone and cake flows through the
//     same picker as every other category. Per-recipient hash variation
//     (MS525) keeps each team member's cake distinct.
//   - Caller passes a `SupabaseClient` so the same function runs in
//     both browser and Deno edge contexts.
//
// v2 falls back to v1 generic copy whenever no DB row matches —
// keeps the surface alive even if the catalogue is sparse for a
// category. The fallback is silent on the customer side; the
// rationaleDetail still reads naturally.

import {
  TIER_TO_VIBE,
  PRICING,
  BIG_DAYS_STARTING_PRICE,
} from "./catalogue.ts";
import type { BakeryName, TierName, Vibe } from "./catalogue.ts";

/* ─── Vendor category routing (v2) ─────────────────────────────── */

// MS73: "meal" added so the engine can route meal-bento / dinner SKUs
// (already in vendor_products per migration 20260512010000's CHECK
// constraint, and supported across Browse/Vendors/FilterRail/chat-
// concierge tool enum). Without it here, the resolver typed `meal`
// out of bounds and a "send Sarah a meal voucher" brief from the chat
// surface would silently fall back to v1 cake.
// MS455: "coffee" was missing — Patricia Coffee / Proud Mary's / ST. ALi
// SKUs were unreachable via the recommendation engine (pickSkuForCategory
// filters by vendor_category; if "coffee" isn't in this array the TS type
// system rejects it upstream and the SKU picker never queries for it).
// Same MS278 drift pattern as MS449–452 (chat enum, labels, admin UI).
export const RECOMMENDATION_CATEGORIES = [
  "cake",
  "hamper",
  "florist",
  "workshop",
  "spa",
  "experience",
  "meal",
  "coffee",
] as const;
export type RecommendationCategory = typeof RECOMMENDATION_CATEGORIES[number];

// Occasion → vendor_category default. Existing occasions all map to
// "cake" so v2 stays backward-compatible: the cake path delegates to
// v1 unchanged. New occasion strings (sympathy / wellness / workshop /
// experience / hamper / thank_you) route to the corresponding new
// vendor categories.
//
// Callers may override with `RecommendationInput.categoryHint` — the
// hint always wins over the occasion default. UIs that don't expose
// the new occasion types can still use the new categories by passing
// `categoryHint: 'florist'` etc.
export const OCCASION_TO_CATEGORY: Record<string, RecommendationCategory> = {
  // Existing occasions (catalogue.ts CELEBRATION_TYPES) — all cake-led
  birthday: "cake",
  work_anniversary: "cake",
  welcome: "cake",
  farewell: "cake",
  milestone: "cake",
  other: "cake",
  // Aliases used by OrderConcierge/Dashboard occasion-label helpers
  anniversary: "cake",
  "first day": "cake",
  // New occasion types unlocked by the v6 vendor seed (2026-05-12).
  // UI doesn't surface these yet — engine is ready when it does.
  sympathy: "florist",
  thank_you: "hamper",
  wellness_gift: "spa",
  team_workshop: "workshop",
  experience_gift: "experience",
  // MS73: meal-led occasions. UI surfaces these as freeform briefs
  // through chat-concierge today (no enum); the engine routes them
  // to vendor_category=meal once SKUs are seeded.
  team_lunch: "meal",
  catered_dinner: "meal",
};

// Minimal Supabase client surface — we only need .from(...).select(...)
// chaining and the standard {data, error} return shape. Typed as a
// shallow alias so this file stays Deno-compatible without pulling
// the full @supabase/supabase-js types into edge functions; concrete
// callers (browser + edge) pass their real SupabaseClient and TS
// accepts via structural compatibility.
//
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type RecommendationSupabaseClient = any;

interface DbProductRow {
  id: string;
  name: string;
  description: string | null;
  category: string | null;
  price: number;
  dietary_tags: string[] | null;
  lead_time_days: number | null;
  is_voucher: boolean;
  delivery_zones: string[] | null;
  vendor_category: RecommendationCategory;
  image_url: string | null;
  source_url: string | null;
  vendor_partner_id: string;
}

export type RecommendationTier = "standard" | "premium" | "luxury";

/** Profile gift_preferences jsonb on team_members. */
export interface GiftPreferencesHints {
  vibes?: string[];
  avoid?: string[];
  desired_categories?: string[];
  notes?: string;
}

export interface RecommendationInput {
  tier: RecommendationTier;
  teamSize: number;
  /** Free-form dietary tags (Gluten-free, Vegan, Dairy-free, ...) */
  dietaries: string[];
  /** birthday | work_anniversary | welcome | farewell | milestone |
   *  other | sympathy | thank_you | wellness_gift | team_workshop |
   *  experience_gift. The new occasions route to non-cake categories
   *  via OCCASION_TO_CATEGORY. */
  occasion: string;
  recipientName: string;
  /** ISO date the celebration falls on. */
  date: string;
  /** Optional delivery address — copied through to the output. */
  deliveryAddress?: string;
  /** Optional delivery-window key from the form
   *  (morning_early | morning_late | afternoon). */
  deliveryWindow?: string;
  /** Free-text customer notes — engine doesn't consume these yet,
   *  but they're surfaced to the operator behind the scenes. */
  notes?: string;
  /** v2 only. Explicit override for the vendor category to query.
   *  When set, takes precedence over the OCCASION_TO_CATEGORY default.
   *  Use cases: a UI that lets the customer flip between "send a cake"
   *  vs "send flowers" for the same occasion. */
  categoryHint?: RecommendationCategory;
  /** Profile enrichment from team_members — hobbies, foods, gift prefs. */
  hobbies?: string[];
  favourite_foods?: string[];
  gift_preferences?: GiftPreferencesHints;
  personal_notes?: string;
}

export interface RecommendationOutput {
  bakery: BakeryName;
  tier: RecommendationTier;
  productSummary: string;
  productLine: string;
  cardDetail: string;
  deliveryDetail: string;
  rationaleDetail: string;
  totalCents: number;
  imageUrl: string;
  imageAlt: string;
  // MS70: resolved vendor_category so the approval modal can render
  // the right per-category icon (cake → 🎂, hamper → 🧺, florist →
  // 🌸, etc.) via categoryEmoji(). v1 sync path always returns "cake"
  // (its bakery routing is cake-only); v2 returns the actual SKU
  // category from the DB query.
  vendorCategory: RecommendationCategory;
  // MS95: resolved vendor_products.id. v1 sync path always returns
  // null (its bakery output is curated copy, not a SKU). v2 async
  // returns the row.id of the picked SKU so callers can write it
  // into celebration_recommendations.line_items[].sku_id — which
  // is what useOrders' image_url enrichment, OrderDetail's rich
  // SKU rendering, and MemberHistorySection all read from. Pre-MS95
  // the engine picked a SKU internally but threw the id away, so
  // every approval's line_items stayed empty and downstream SKU-
  // aware UIs couldn't resolve anything.
  skuId: string | null;
}

const TIER_DB_TO_NAME: Record<RecommendationTier, TierName> = {
  standard: "Standard",
  premium: "Premium",
  luxury: "Luxury",
};

// v1 demo images per tier — Unsplash CDN, with the modal's HeroPhoto
// component falling back gracefully if a URL 404s. v2 reads from
// vendor_products.image_url for the picked product.
const TIER_IMAGES: Record<RecommendationTier, { url: string; alt: string }> = {
  standard: {
    url: "https://images.unsplash.com/photo-1599785209707-a456fc1337bb?w=1200&q=80&auto=format&fit=crop",
    alt: "A dozen assorted cupcakes",
  },
  premium: {
    url: "https://images.unsplash.com/photo-1535254973040-607b474cb50d?w=1200&q=80&auto=format&fit=crop",
    alt: "A signature celebration cake",
  },
  luxury: {
    url: "https://images.unsplash.com/photo-1488477181946-6428a0291777?w=1200&q=80&auto=format&fit=crop",
    alt: "A curated patisserie spread",
  },
};

// Team size → PRICING bucket (matches TEAM_SIZE_OPTIONS in catalogue).
function bucketForTeamSize(size: number): "team" | "floor" | "office" | "big_days" {
  if (size <= 12) return "team";
  if (size <= 22) return "floor";
  if (size <= 35) return "office";
  return "big_days";
}

// Bakery picker for Standard tier. Defaults to Cupcake Central (best
// dietary range), routes to Little Cupcakes when the brief explicitly
// asks for vegan (LC has the dedicated vegan range; CC's vegan
// coverage is thinner).
function pickStandardBakery(dietaries: string[]): BakeryName {
  const wantsVegan = dietaries.some((d) => d.toLowerCase().includes("vegan"));
  if (wantsVegan) return "Little Cupcakes";
  // Goldelucks is intentionally NOT picked by default — donuts-only
  // doesn't suit every team. Customer can adjust per-celebration to
  // get GL once we wire that.
  return "Cupcake Central";
}

function pickBakery(input: RecommendationInput): BakeryName {
  switch (input.tier) {
    case "standard":
      return pickStandardBakery(input.dietaries);
    case "premium":
      return "Le Yeahllow";
    case "luxury":
      return "Chéri";
  }
}

// Bakery-specific product copy. v1 hardcodes a representative product
// per bakery; v2 will pick a specific vendor_products row and pull
// name + description directly from the catalogue.
function getProductCopy(bakery: BakeryName): {
  summary: string;
  line: (recipientFirst: string, dietaries: string[]) => string;
} {
  switch (bakery) {
    case "Cupcake Central":
      return {
        summary: "A dozen assorted cupcakes",
        line: (first, diets) => {
          const dietHint =
            diets.length > 0
              ? ` Plus options for ${diets.map((d) => d.toLowerCase()).join(", ")} so everyone on ${first}'s team is covered.`
              : "";
          return `A dozen cupcakes from Cupcake Central in Richmond — chocolate, vanilla, red velvet.${dietHint}`;
        },
      };
    case "Little Cupcakes":
      return {
        summary: "Custom-branded cupcakes",
        line: (_first, diets) => {
          const dietHint =
            diets.length > 0
              ? ` Includes ${diets.map((d) => d.toLowerCase()).join(", ")} options for the team.`
              : "";
          return `Custom-branded cupcakes from Little Cupcakes — assorted styles with the option for vegan and corporate variants.${dietHint}`;
        },
      };
    case "Goldelucks":
      return {
        summary: "A donut party box",
        line: (first) => `A donut party box from Goldelucks — assorted flavours and toppings, sized for ${first}'s team.`,
      };
    case "Le Yeahllow":
      return {
        summary: "A signature celebration cake",
        line: (_first, diets) => {
          const dietHint =
            diets.length > 0
              ? ` Le Yeahllow accommodates ${diets.map((d) => d.toLowerCase()).join(", ")} on request.`
              : "";
          return `A signature celebration cake from Le Yeahllow's premium catalogue — your choice of mousse, layer, or signature style.${dietHint}`;
        },
      };
    case "Chéri":
      return {
        summary: "A curated patisserie spread",
        line: (first) => `A curated patisserie spread from Chéri — Grand Gâteaux and individual desserts for ${first}'s team.`,
      };
  }
}

function buildRationale(input: RecommendationInput, bakery: BakeryName): string {
  const dietCount = input.dietaries.length;
  if (dietCount > 0) {
    return `${bakery} handles ${dietCount} dietary requirement${dietCount > 1 ? "s" : ""} cleanly at this tier without compromising the spread for the rest of the team.`;
  }
  return `${bakery} is our default pick for this tier — proven reliable, sized right for a team of ${input.teamSize}.`;
}

function formatDeliveryWindow(window: string | undefined): string {
  const m: Record<string, string> = {
    morning_early: "9 – 10 am, on the day",
    morning_late: "10 – 11 am, on the day",
    afternoon: "2 – 3 pm, on the day",
  };
  if (!window) return "On the day";
  return m[window] ?? "On the day";
}

/**
 * v1 entry point. Given a brief, returns a concrete recommendation
 * shaped for the approval modal. Deterministic — same inputs always
 * yield the same output (no randomness, no time-of-day variation).
 */
export function generateRecommendation(input: RecommendationInput): RecommendationOutput {
  const tier = input.tier;
  const tierName = TIER_DB_TO_NAME[tier];
  const vibe: Vibe = TIER_TO_VIBE[tierName];
  const bucket = bucketForTeamSize(input.teamSize);

  const bakery = pickBakery(input);

  // PRICING grid lookup; big_days falls back to the headline anchor.
  const pricedAud = PRICING[bucket][vibe] ?? BIG_DAYS_STARTING_PRICE;

  const copy = getProductCopy(bakery);
  const firstName = input.recipientName.trim().split(" ")[0] || "your colleague";

  const img = TIER_IMAGES[tier];

  return {
    bakery,
    tier,
    productSummary: copy.summary,
    productLine: copy.line(firstName, input.dietaries),
    cardDetail: "A personalised card included with the delivery.",
    deliveryDetail: formatDeliveryWindow(input.deliveryWindow),
    rationaleDetail: buildRationale(input, bakery),
    totalCents: pricedAud * 100,
    imageUrl: img.url,
    imageAlt: img.alt,
    // MS70: v1 routes everything through the cake bakeries — its
    // hand-curated 5-bakery output is cake-only by definition.
    vendorCategory: "cake",
    // MS95: v1's bakery output is curated copy (productSummary +
    // bakery + tier) not a real vendor_products row — there's no
    // SKU id to expose. Callers must treat the recommendation as
    // narrative-only and route any purchase through chat/order to
    // resolve a real SKU. v2 below populates this with row.id.
    skuId: null,
  };
}

/* ─── v2 (async, DB-backed) ─────────────────────────────────────── */

// Resolve the vendor category to query. Hint always wins over occasion
// default; gift_preferences.desired_categories[0] wins over occasion
// when valid; occasion default falls back to "cake" for unknown strings.
export function resolveCategory(input: RecommendationInput): RecommendationCategory {
  if (input.categoryHint) return input.categoryHint;
  const desired = input.gift_preferences?.desired_categories?.[0];
  if (
    desired &&
    (RECOMMENDATION_CATEGORIES as readonly string[]).includes(desired)
  ) {
    return desired as RecommendationCategory;
  }
  return OCCASION_TO_CATEGORY[input.occasion] ?? "cake";
}

// Days until the celebration date. Used to filter by lead_time_days
// (vendors that need 7 days lead can't fulfill a 3-day-out order).
function daysUntil(dateIso: string): number {
  const target = new Date(dateIso).getTime();
  const now = Date.now();
  return Math.max(0, Math.floor((target - now) / (1000 * 60 * 60 * 24)));
}

// Friendly category labels for the rationale string. Used in the
// fallback copy when no DB SKU was found AND the v1 cake path didn't
// run (i.e. the customer asked for a category we have zero rows for —
// shouldn't happen post-seed but guards against catalogue gaps).
const CATEGORY_LABEL: Record<RecommendationCategory, string> = {
  cake: "cake",
  hamper: "curated hamper",
  florist: "flowers",
  workshop: "workshop voucher",
  spa: "spa experience",
  experience: "experience",
  meal: "chef-prepared meal",
  // MS455: coffee voucher label for fallback copy.
  coffee: "coffee voucher",
};

// MS525: tiny stable string hash (DJB2). Used to pick a recipient-
// specific SKU out of the qualified candidate pool. Stable across runs
// (same input → same output) so a given recipient keeps the same pick
// for the same celebration, while different recipients in the same
// company get different picks.
function hashString(s: string): number {
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) | 0;
  return Math.abs(h);
}

function skuHaystack(row: DbProductRow): string {
  return [row.name, row.description, row.category]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
}

const AVOID_CATEGORY_MAP: Partial<Record<string, RecommendationCategory[]>> = {
  alcohol: ["hamper", "meal"],
  flowers: ["florist"],
  strong_fragrances: ["florist", "spa"],
  novelty_gifts: ["workshop", "experience"],
  spicy_food: ["meal", "hamper"],
};

type ProfileScoringInput = Pick<
  RecommendationInput,
  "hobbies" | "favourite_foods" | "gift_preferences" | "personal_notes"
>;

function hasProfileSignals(hints: ProfileScoringInput): boolean {
  const gp = hints.gift_preferences;
  return (
    (hints.hobbies ?? []).length > 0 ||
    (hints.favourite_foods ?? []).length > 0 ||
    (hints.personal_notes?.trim().length ?? 0) > 0 ||
    (gp?.vibes ?? []).length > 0 ||
    (gp?.avoid ?? []).length > 0 ||
    (gp?.desired_categories ?? []).length > 0 ||
    (gp?.notes?.trim().length ?? 0) > 0
  );
}

/** Rank SKUs by how well they match recipient profile enrichment signals. */
export function scoreSkuForProfile(
  row: DbProductRow,
  hints: ProfileScoringInput,
): number {
  let score = 0;
  const haystack = skuHaystack(row);

  for (const hobby of hints.hobbies ?? []) {
    const token = hobby.toLowerCase().trim();
    if (token && haystack.includes(token)) score += 3;
  }

  for (const food of hints.favourite_foods ?? []) {
    const token = food.toLowerCase().trim();
    if (token && haystack.includes(token)) score += 2;
  }

  const gp = hints.gift_preferences;
  for (const vibe of gp?.vibes ?? []) {
    const token = vibe.toLowerCase().replace(/_/g, " ").trim();
    if (token && haystack.includes(token)) score += 2;
  }

  for (const word of (gp?.notes ?? "").toLowerCase().split(/\s+/).filter((w) => w.length > 3)) {
    if (haystack.includes(word)) score += 1;
  }

  for (const word of (hints.personal_notes ?? "")
    .toLowerCase()
    .split(/\s+/)
    .filter((w) => w.length > 3)
    .slice(0, 8)) {
    if (haystack.includes(word)) score += 1;
  }

  for (const avoid of gp?.avoid ?? []) {
    const norm = avoid.toLowerCase().trim().replace(/[-\s]+/g, "_");
    const blocked = AVOID_CATEGORY_MAP[norm] ?? [];
    if (blocked.includes(row.vendor_category)) score -= 10;
    const token = avoid.toLowerCase().replace(/_/g, " ").trim();
    if (token && haystack.includes(token)) score -= 5;
  }

  return score;
}

function profileMatchLabels(
  row: DbProductRow,
  hints: ProfileScoringInput,
): string[] {
  const haystack = skuHaystack(row);
  const labels: string[] = [];

  for (const hobby of hints.hobbies ?? []) {
    const token = hobby.toLowerCase().trim();
    if (token && haystack.includes(token)) labels.push(hobby);
  }
  for (const food of hints.favourite_foods ?? []) {
    const token = food.toLowerCase().trim();
    if (token && haystack.includes(token)) labels.push(food);
  }
  for (const vibe of hints.gift_preferences?.vibes ?? []) {
    const token = vibe.toLowerCase().replace(/_/g, " ").trim();
    if (token && haystack.includes(token)) labels.push(vibe.replace(/_/g, " "));
  }

  return [...new Set(labels)].slice(0, 2);
}

// Pick one SKU from vendor_products for the requested category. Filter
// by tier ceiling (price ≤ ceilingCents/100) and lead time (a vendor's
// stated minimum lead must fit inside the days-until window). Order by
// price DESC so we land on the most generous SKU the budget supports —
// the customer asked for "Premium," they should get the top of the
// Premium band, not the floor.
//
// MS98: dietaries soft-prefer is now actually implemented. The doc
// comment had been promising "prefer SKUs whose dietary_tags include
// at least one match" since v2 landed, but the function never accepted
// the parameter. Now: when dietaries are provided AND the category is
// consumable (cake / hamper / meal — MS60 rule says vouchers/spa/etc
// have no meaningful dietary axis), filter to SKUs that match at least
// one tag; fall back to the unfiltered top-priced SKU when none match
// (soft-prefer, not hard-filter — sparse catalogue can otherwise zero
// out a buyer with a niche diet).
//
// MS525: when called with a `recipientName`, hash-shuffle the top half
// of qualified candidates so different recipients in the same company
// on the same date get different SKUs. The top-half rule preserves
// tier semantics — a "Premium" buyer never lands on the cheapest SKU
// just because it hashed to position 0. Pre-MS525 every recipient in
// a company landed on the same `candidates[0]`, making every dashboard
// tile read "A dozen assorted cupcakes from Cupcake Central." Callers
// that don't pass `recipientName` (chat-concierge, scheduled jobs)
// keep the deterministic top-priced behaviour.
async function pickSkuForCategory(
  supabase: RecommendationSupabaseClient,
  category: RecommendationCategory,
  ceilingCents: number,
  daysOut: number,
  dietaries: string[] = [],
  recipientName: string | null = null,
  profileHints: ProfileScoringInput = {},
): Promise<DbProductRow | null> {
  const ceilingAud = ceilingCents / 100;
  const { data, error } = await supabase
    .from("vendor_products")
    .select(
      "id, name, description, category, price, dietary_tags, lead_time_days, is_voucher, delivery_zones, vendor_category, image_url, source_url, vendor_partner_id",
    )
    .eq("vendor_category", category)
    .is("deleted_at", null)
    .eq("is_available", true)
    .is("parent_product_id", null)
    .lte("price", ceilingAud)
    .order("price", { ascending: false })
    .limit(20);

  if (error) {
    // Don't throw — let the caller fall back to v1 generic copy. The
    // engine should never crash a customer's order flow over a DB
    // hiccup; we'd rather show generic copy than an error screen.
    console.warn(`[recommendation v2] DB query failed for category ${category}:`, error.message);
    return null;
  }

  const rows = (data ?? []) as DbProductRow[];
  if (rows.length === 0) return null;

  // Lead-time filter (in JS, not SQL — null lead_time_days is treated
  // as "same-day eligible" so it always passes).
  const inLead = rows.filter((r) => (r.lead_time_days ?? 0) <= daysOut);
  let candidates = inLead.length > 0 ? inLead : rows;

  // MS98: dietary soft-prefer for consumable categories. Normalises
  // the lookup (lowercase + underscore vs hyphen) since callers pass
  // free-form labels (e.g. "Gluten-free", "Vegan") but vendor_products
  // stores canonical lowercase ("gluten_free", "vegan").
  const isConsumable = category === "cake" || category === "hamper" || category === "meal";
  if (isConsumable && dietaries.length > 0) {
    const wanted = new Set(
      dietaries
        .map((d) => d.toLowerCase().trim().replace(/[-\s]+/g, "_"))
        .filter(Boolean),
    );
    const dietMatch = candidates.filter((r) => {
      const tags = Array.isArray(r.dietary_tags) ? r.dietary_tags : [];
      return tags.some((t) => wanted.has(t.toLowerCase()));
    });
    if (dietMatch.length > 0) candidates = dietMatch;
    // else: silently fall back to the unfiltered top — soft-prefer.
  }

  // Profile-aware ranking: reorder by enrichment signal matches, then
  // price, before the per-recipient hash shuffle.
  if (hasProfileSignals(profileHints)) {
    candidates = [...candidates].sort((a, b) => {
      const scoreDiff = scoreSkuForProfile(b, profileHints) - scoreSkuForProfile(a, profileHints);
      if (scoreDiff !== 0) return scoreDiff;
      return b.price - a.price;
    });
  }

  // MS525: per-recipient hash-shuffle. Pick from the top half (highest-
  // priced under the ceiling) so tier semantics hold — a Premium buyer
  // still gets a premium-band SKU, just not always the SAME premium-
  // band SKU as every other Premium teammate in their company.
  if (recipientName && candidates.length > 1) {
    const hash = hashString(`${recipientName.toLowerCase().trim()}:${category}`);
    const topHalfSize = Math.max(1, Math.ceil(candidates.length / 2));
    const topHalf = candidates.slice(0, topHalfSize);
    return topHalf[hash % topHalf.length];
  }

  return candidates[0];
}

// Bakery name lookup for the display field. We could query
// vendor_partners directly, but joining inline doubles the round-trip;
// caller can pass in the resolved name via the wrapper. For now keep
// it inline: one extra query, simple shape.
async function lookupBakeryName(
  supabase: RecommendationSupabaseClient,
  partnerId: string,
): Promise<string> {
  const { data } = await supabase
    .from("vendor_partners")
    .select("vendor_name")
    .eq("id", partnerId)
    .limit(1)
    .single();
  return data?.vendor_name ?? "Lembra partner";
}

// Build the customer-facing product line copy from a real DB row.
// This is what makes v2 feel different from v1 — instead of the
// generic per-bakery sentence, we name the actual SKU (e.g. "A
// Sympathy Comfort hamper from Cremorne Street Hampers — calming
// items and house-baked goods.").
function buildDbProductLine(
  row: DbProductRow,
  bakeryName: string,
  recipientFirstName: string,
  category: RecommendationCategory,
): { summary: string; line: string } {
  const summary = row.name;
  const desc = row.description?.trim() || `${CATEGORY_LABEL[category]} from ${bakeryName}`;

  // Voucher SKUs read differently — they're a code, not a delivery.
  // The success-state copy in ApprovalModal already handles the
  // voucher case via tier; productLine just needs to read naturally.
  if (row.is_voucher) {
    return {
      summary,
      line: `A ${row.name} from ${bakeryName} — ${desc}. ${recipientFirstName} receives a voucher to redeem on their own time.`,
    };
  }

  return {
    summary,
    line: `A ${row.name} from ${bakeryName} — ${desc}.`,
  };
}

function buildDbRationale(
  row: DbProductRow,
  bakeryName: string,
  category: RecommendationCategory,
  input: RecommendationInput,
): string {
  const dietaries = input.dietaries;
  const matches = (row.dietary_tags ?? []).filter((t) =>
    dietaries.some((d) => d.toLowerCase().includes(t.toLowerCase())),
  );
  const profileLabels = profileMatchLabels(row, input);

  if (matches.length > 0) {
    let rationale = `${bakeryName} is the strongest ${CATEGORY_LABEL[category]} in the price band that covers ${matches.join(", ").toLowerCase()}.`;
    if (profileLabels.length > 0) {
      rationale += ` Matches ${profileLabels.join(" and ")} from their profile.`;
    }
    return rationale;
  }

  if (profileLabels.length > 0) {
    return `Picked for ${profileLabels.join(" and ")} — ${bakeryName}'s ${row.name} fits what you've told us about them.`;
  }

  return `${bakeryName} is our top pick for a ${CATEGORY_LABEL[category]} in this price band — proven Melbourne supplier, ships reliably.`;
}

/**
 * v2 entry point. Async — needs a Supabase client to read the live
 * catalogue. Routes by occasion (or explicit categoryHint) to a
 * vendor_category, queries vendor_products for the best-fit SKU,
 * and returns a DB-backed recommendation.
 *
 * Falls back to v1 sync `generateRecommendation()` only when no DB
 * row matches the filter (price ceiling, lead time, category) —
 * we'd rather show generic copy than an empty modal.
 *
 * MS525: cake used to short-circuit to v1 because home-bakery treats
 * weren't in vendor_products. They're seeded now
 * (20260513050000_seed_home_bakery_skus_into_vendor_products.sql),
 * so cake flows through the same picker as every other category,
 * which unlocks per-recipient variation via the hash-shuffle.
 */
export async function generateRecommendationFromCatalogue(
  input: RecommendationInput,
  supabase: RecommendationSupabaseClient,
): Promise<RecommendationOutput> {
  const category = resolveCategory(input);

  // Compute the price ceiling from the existing PRICING grid, same way
  // v1 does. Categories share the customer's tier ceiling — a "Premium"
  // sympathy bouquet draws from the same budget band as a "Premium"
  // cake would.
  const tierName = TIER_DB_TO_NAME[input.tier];
  const vibe: Vibe = TIER_TO_VIBE[tierName];
  const bucket = bucketForTeamSize(input.teamSize);
  const pricedAud = PRICING[bucket][vibe] ?? BIG_DAYS_STARTING_PRICE;
  const ceilingCents = pricedAud * 100;
  const daysOut = daysUntil(input.date);

  // MS98: pass through the recipient's dietaries so the picker can
  // soft-prefer matching SKUs (MS60 rule: dietaries only apply to
  // consumable categories; the picker itself enforces that gate).
  // MS525: pass recipientName so the picker can hash-shuffle the top
  // half of candidates — different recipients get different SKUs.
  const row = await pickSkuForCategory(
    supabase,
    category,
    ceilingCents,
    daysOut,
    input.dietaries,
    input.recipientName,
    {
      hobbies: input.hobbies,
      favourite_foods: input.favourite_foods,
      gift_preferences: input.gift_preferences,
      personal_notes: input.personal_notes,
    },
  );
  if (!row) {
    // No SKU available — fall back to v1 generic so the modal still
    // renders. Operator sees the gap via empty vendor_products query
    // logs; customer sees a plausible cake recommendation instead of
    // an error.
    console.warn(`[recommendation v2] no SKU for category ${category}, falling back to v1`);
    return withResolvedSkuId(supabase, generateRecommendation(input));
  }

  const bakeryName = await lookupBakeryName(supabase, row.vendor_partner_id);
  const firstName = input.recipientName.trim().split(" ")[0] || "your colleague";
  const copy = buildDbProductLine(row, bakeryName, firstName, category);
  const rationale = buildDbRationale(row, bakeryName, category, input);

  return {
    // The BakeryName type is the union of the 5 home bakeries; new
    // vendors aren't in that union. Cast through string so TS doesn't
    // complain — the field is consumed as a display string everywhere.
    bakery: bakeryName as BakeryName,
    tier: input.tier,
    productSummary: copy.summary,
    productLine: copy.line,
    cardDetail: row.is_voucher
      ? "Voucher arrives by email with a personalised note attached."
      : "A personalised card included with the delivery.",
    deliveryDetail: formatDeliveryWindow(input.deliveryWindow),
    rationaleDetail: rationale,
    // Use the actual SKU price, not the tier ceiling — gives the
    // customer the real number for what we're picking.
    totalCents: Math.round(row.price * 100),
    imageUrl: row.image_url ?? TIER_IMAGES[input.tier].url,
    imageAlt: row.image_url ? row.name : TIER_IMAGES[input.tier].alt,
    // MS70: surface the resolved category so downstream UIs can pick
    // the right per-category emoji / framing without re-deriving from
    // SKU shape.
    vendorCategory: category,
    // MS95: the actual vendor_products.id we just picked. Callers
    // can record this in celebration_recommendations.line_items so
    // useOrders.enrichOrdersWithImageUrl + OrderDetail's
    // RichSkuDetailSection + MemberHistorySection can all resolve
    // back to the real SKU.
    skuId: row.id,
  };
}

/* ─── MS606: sku_id hydration for v1 / fallback approvals ───────── */

/** v1 productSummary stubs → canonical vendor_products.name per bakery. */
const V1_STUB_CATALOG_NAMES: Record<string, Partial<Record<BakeryName, string>>> = {
  "A dozen assorted cupcakes": { "Cupcake Central": "Cupcake Gift Box (12)" },
  "Custom-branded cupcakes": { "Little Cupcakes": "Standard Cupcakes (12)" },
  "A donut party box": { "Goldelucks": "Donut Party Box (15)" },
  "A signature celebration cake": { "Le Yeahllow": "Yuzu Hachimitsu (6-inch)" },
  "A curated patisserie spread": { "Chéri": "Eye Candy Grand Gâteau" },
};

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export interface ResolveRecommendationSkuInput {
  // Loosened to the values callers actually hold (ApprovalRecommendation carries
  // skuId as string|null|undefined and vendorCategory as a plain string). Both are
  // used defensively (skuId is UUID-tested; vendorCategory is only an
  // `.eq("vendor_category", …)` filter hint), so widening is safe.
  skuId?: string | null;
  bakery: string;
  productSummary: string;
  vendorCategory?: RecommendationCategory | string | null;
}

/**
 * Resolve vendor_products.id when the engine already picked a SKU (v2)
 * or when v1 curated copy can be matched by bakery + product name.
 * Used before writing celebration_recommendations.line_items on approval.
 */
export async function resolveRecommendationSkuId(
  supabase: RecommendationSupabaseClient,
  input: ResolveRecommendationSkuInput,
): Promise<string | null> {
  if (input.skuId && UUID_RE.test(input.skuId)) return input.skuId;

  const bakery = input.bakery?.trim();
  const summary = input.productSummary?.trim();
  if (!bakery || !summary) return null;

  const stubName =
    V1_STUB_CATALOG_NAMES[summary]?.[bakery as BakeryName] ?? summary;

  const { data: partner, error: partnerErr } = await supabase
    .from("vendor_partners")
    .select("id")
    .eq("vendor_name", bakery)
    .limit(1)
    .maybeSingle();

  if (partnerErr || !partner?.id) return null;

  const baseFilter = () =>
    supabase
      .from("vendor_products")
      .select("id")
      .eq("vendor_partner_id", partner.id)
      .is("deleted_at", null)
      .eq("is_available", true)
      .is("parent_product_id", null);

  const { data: exact } = await baseFilter().eq("name", stubName).limit(1).maybeSingle();
  if (exact?.id && UUID_RE.test(exact.id)) return exact.id;

  if (input.vendorCategory) {
    const { data: byCategory } = await baseFilter()
      .eq("vendor_category", input.vendorCategory)
      .ilike("name", stubName)
      .limit(1)
      .maybeSingle();
    if (byCategory?.id && UUID_RE.test(byCategory.id)) return byCategory.id;
  }

  const { data: fuzzy } = await baseFilter().ilike("name", stubName).limit(1).maybeSingle();
  if (fuzzy?.id && UUID_RE.test(fuzzy.id)) return fuzzy.id;

  return null;
}

async function withResolvedSkuId(
  supabase: RecommendationSupabaseClient,
  output: RecommendationOutput,
): Promise<RecommendationOutput> {
  if (output.skuId && UUID_RE.test(output.skuId)) return output;
  const skuId = await resolveRecommendationSkuId(supabase, {
    skuId: output.skuId,
    bakery: output.bakery,
    productSummary: output.productSummary,
    vendorCategory: output.vendorCategory,
  });
  return skuId ? { ...output, skuId } : output;
}
